import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {select} from '@angular-redux/store';
import {FilterCriteria} from '../store/IAppState';
import {isFeatureEnabled} from '../shared/helpers';
import * as _ from 'lodash';
import {FeatureToggleEntries} from '../shared/feature-toggle-settings-enum';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {EmDimension, FeatureToggleSetting, PdfExportData, UserProfile} from '../shared/models';
import {BatchExportDialogComponent} from '../batch-export/batch-export-dialog/batch-export-dialog.component';
import {ExportDialogComponent} from '../export-dialog/export-dialog.component';
import {ExportDialogMultilevelComponent} from '../export-dialog-multilevel/export-dialog-multilevel.component';
import {
  MultiProviderExportRedesignComponent
} from '../batch-export/multi-provider-export-redesign/multi-provider-export-redesign.component';

const EXPORT_DIALOG_PANEL_CLASS = 'export-dialog';

@Component({
  selector: 'app-export-button',
  templateUrl: './export-button.component.html',
  styleUrls: ['./export-button.component.scss']
})
export class ExportButtonComponent implements OnInit, OnDestroy {

  pageLoading = true;
  showOptions = false;
  multiProviderExportEnabled = false;
  canShowHoverMessage = false;
  canShowUpdatedExport = false;
  canShowRedesignMultiproviderExport = false;

  @Output() afterPdfExport = new EventEmitter<[PdfExportData, EmDimension[], boolean]>();

  @select(['filters'])
  private readonly filters$: Observable<FilterCriteria>;
  @select(['data', 'featureToggleSettings'])
  private readonly featureToggleSettings$: Observable<FeatureToggleSetting[]>;
  @select(['data', 'userProfile'])
  private readonly userProfile$: Observable<UserProfile>;

  pdfExportSubscription: Subscription;

  constructor(public dialog: MatDialog) { }

  ngOnInit() {
    combineLatest([this.filters$.pipe(distinctUntilChanged((type1, type2) =>
      _.isEqual(type1, type2))), this.featureToggleSettings$, this.userProfile$])
      .subscribe(([filters, featureToggleSettings, userProfile]: [FilterCriteria, FeatureToggleSetting[],
        UserProfile]) => {
        this.canShowUpdatedExport = isFeatureEnabled(FeatureToggleEntries.UPDATED_EXPORT, featureToggleSettings, userProfile);
        this.canShowRedesignMultiproviderExport = isFeatureEnabled(FeatureToggleEntries.REDESIGN_MULTIPROVIDER_EXPORT,
          featureToggleSettings, userProfile);
        this.pageLoading = true;
        this.setShouldEnableExport(filters.nodePath);
      });
  }

  ngOnDestroy(): void {
    if (this.pdfExportSubscription) {
      this.pdfExportSubscription.unsubscribe();
    }
  }

  setShouldEnableExport(nodePath: string): void {
    const allNodePaths: string[] = nodePath.split('|');
    if (allNodePaths.length === 1 && allNodePaths[0].split('\\').length === 3) {
      this.multiProviderExportEnabled = true;
    } else {
      const firstNodePath = allNodePaths[0].split('\\');
      if (firstNodePath.length === 2) {
        this.multiProviderExportEnabled = false;
      } else {
        this.multiProviderExportEnabled = !allNodePaths.find(path => {
          const asArray: string[] = path.split('\\');
          return asArray[2] !== firstNodePath[2];
        });
      }
    }
  }

  toggleShowHover(show: boolean): void {
    this.canShowHoverMessage = show;
  }

  openExportOptions(show: boolean): void {
     this.showOptions = show;
  }

  deactivateShowOptions(): void {
    this.showOptions = false;
  }

  openPageExport(): void {
    const dialogRef = this.canShowUpdatedExport ? this.dialog.open(ExportDialogMultilevelComponent) :
      this.dialog.open(ExportDialogComponent, {panelClass: EXPORT_DIALOG_PANEL_CLASS});
    // @ts-ignore
    this.pdfExportSubscription = dialogRef.componentInstance.afterPdfExport
      .subscribe((exportData: [PdfExportData, EmDimension[], boolean]) => {
        this.afterPdfExport.emit(exportData);
      });
  }

  openMultiProviderExport(): void {
    if (!this.multiProviderExportEnabled) {
      return;
    }
    if (this.canShowRedesignMultiproviderExport) {
      this.dialog.open(MultiProviderExportRedesignComponent, { panelClass: 'mpe-container'});
    } else {
      this.dialog.open(BatchExportDialogComponent);
    }
  }
}
